import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnergyBalance } from './EnergyBalance.js';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  NUTRITION_RULES,
  addDays,
  proteinGap,
  dayTimeline,
  isWorkoutMeal,
  plannedLogId,
  recentMeals,
  totalsWithPending,
  wallTimeInZone,
} from '@kraftwerk/shared';
import type { DaySlot, DaySummary, MacroTargets, MealSetting } from '@kraftwerk/shared';
import { db, ignoreClosed } from '../../db/local.js';
import type {
  LocalEstimateJob,
  LocalFood,
  LocalFoodLogEntry,
  LocalMealPlanEntry,
  LocalRecipe,
} from '../../db/local.js';
import {
  copyMeal,
  daySummaryFor,
  listFoods,
  listLog,
  listPlannedMeals,
  listRecipes,
  logFood,
  logFreeEntry,
  logRecipe,
  logSuggestion,
  planMeal,
  changePlannedAmount,
  relogEntry,
  removeLogEntry,
  restorePlanned,
  skipPlanned,
  skipSlot,
  syncPlannedLog,
  today,
  unskipSlot,
} from '../../db/nutrition.js';
import { acknowledgeIntake, listIntakesForDate } from '../../db/intakes.js';
import { listSkips } from '../../db/meals.js';
import { readProfile } from '../../db/profile.js';
import { upcomingTraining } from '../../db/schedule.js';
import type { UpcomingTraining } from '../../db/schedule.js';
import { useModules } from '../../hooks/useModules.js';
import { MealsSetting } from './MealsSetting.js';
import { WaterTile } from './WaterTile.js';
import { DayNutrients, DayMicros } from './NutrientBalance.js';
import type { DueIntake } from '../../db/intakes.js';
import { MacroBars } from './MacroBars.js';
import { toNumber } from './FoodForm.js';
import { FoodLookup } from './FoodLookup.js';
import type { FoodSuggestion } from './FoodLookup.js';
import { EstimatePlaceholder, PhotoEstimateButton } from './PhotoEstimate.js';
import {
  HouseholdRow,
  MealRulesSetting,
  householdRowIds,
  useHouseholdDay,
} from '../meals/HouseholdDay.js';
import type { HouseholdItem } from '../meals/HouseholdDay.js';
import type { MealContext } from '../../db/householdMeals.js';
import { useIsChild } from '../../hooks/useModules.js';

/**
 * Das Tagesprotokoll — die Ansicht, die täglich benutzt wird.
 *
 * Oben der Tagesstand gegen die Zielwerte, darunter die Slots des Tages.
 * Geplantes zählt als gegessen (18.10.2026) — markiert wird nur, was
 * ausgelassen wurde. Protokolliert wird mit **eingefrorenen** Werten; eine
 * spätere Rezeptänderung darf die Vergangenheit nicht umschreiben.
 */
export function DayLog({
  date,
  targets,
  language,
  timeZone,
}: {
  date: string;
  targets: MacroTargets | null;
  language: 'de' | 'en';
  timeZone: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const child = useIsChild();

  // Supplements desselben Kalendertages — in der Zeitzone des Nutzers, wie die
  // Mahlzeiten daneben.
  const intakes = useLiveQuery(
    () => listIntakesForDate(date, timeZone, db),
    [date, timeZone],
    [] as DueIntake[],
  );

  const summary = useLiveQuery(
    () => (targets ? daySummaryFor(date, targets, db) : Promise.resolve(null)),
    [date, targets?.kcal, targets?.protein, targets?.carbs, targets?.fat],
    null,
  ) as DaySummary | null;

  // Alle Zeilen des Tages in einer Abfrage — auch die weich gelöschten, denn
  // die eines Planeintrags heißen „ausgelassen“. Zwei getrennte Abfragen
  // zogen nacheinander nach, und kurz stand dasselbe Gericht doppelt da.
  const dayRows = useLiveQuery(
    () => db.food_log.where('date').equals(date).toArray(),
    [date],
    [] as LocalFoodLogEntry[],
  );
  const entries = dayRows
    .filter((row) => !row.deletedAt)
    .sort((a, b) => a.eatenAt.localeCompare(b.eatenAt));
  // Geschätzte Zeilen (Foto, KI) machen die Summe ungefähr — das steht dabei.
  const estimated = entries.some((entry) => entry.estimate);
  // Foto-Schätzung im Hintergrund (28.10.2026): Platzhalter zählen in keiner
  // Summe — nicht mit Werten, nicht als 0. Der Tag sagt, dass etwas fehlt.
  const placeholders = useLiveQuery(
    async () =>
      (await db.estimate_jobs.where('date').equals(date).toArray()).sort((a, b) =>
        a.createdAt.localeCompare(b.createdAt),
      ),
    [date],
    [] as LocalEstimateJob[],
  );
  const openNote =
    placeholders.length > 0 ? <OpenEstimatesDay count={placeholders.length} /> : null;
  const plans = useLiveQuery(() => listPlannedMeals(date, db), [date], [] as LocalMealPlanEntry[]);
  const todayDate = today(timeZone);
  const planKey = plans.map((plan) => plan.id).join();
  useEffect(() => {
    syncPlannedLog(date, todayDate, db).catch(ignoreClosed);
  }, [date, todayDate, planKey]);
  // Eigene Mahlzeiten (16.10.2026): Reihenfolge und Uhrzeit aus dem Profil,
  // ausgelassene je Tag, und das Training an seiner Stelle dazwischen.
  const meals = useLiveQuery(async () => (await readProfile(db)).meals, [], undefined);
  const skips = useLiveQuery(() => listSkips(date, db), [date], []);
  const trainingOn = useModules().includes('training');
  const trainings = useLiveQuery(
    async () =>
      trainingOn
        ? (await upcomingTraining(timeZone, 14, db)).filter(
            (entry) => entry.date === date && entry.status === 'planned',
          )
        : [],
    [date, timeZone, trainingOn],
    [] as UpcomingTraining[],
  );
  const skipped = new Set(skips.map((row) => row.slot));
  // Haushalts-Essensplan (27.10.2026): was der Haushalt heute isst, mit
  // meinen Abweichungen — zählt wie Geplantes.
  const household = useHouseholdDay(date, trainings.length > 0);
  const householdIds = household.context
    ? householdRowIds(household.items, household.context.me)
    : new Set<string>();
  const gap =
    household.items.length > 0 && date >= todayDate && targets && summary
      ? proteinGap(summary.progress.consumed.protein, targets.protein)
      : null;

  // Pre/Post stehen nur an Trainingstagen — oder wenn schon etwas darin
  // steht. Supplements zu einer Mahlzeit stehen in deren Kachel; ist die
  // nicht da (oder das Supplement an keine gebunden), steht es zu seiner
  // Uhrzeit im Ablauf.
  const keep = new Set([
    ...entries.map((entry) => entry.slot),
    ...plans.map((plan) => plan.slot),
    ...household.items.map((item) => item.entry.slot!),
    ...placeholders.map((job) => job.slot),
  ]);
  const shown = new Set(
    (meals ?? [])
      .filter((meal) => !isWorkoutMeal(meal.slot) || trainings.length > 0 || keep.has(meal.slot))
      .map((meal) => meal.slot),
  );
  const atMeal = (intake: DueIntake) => intake.mealSlot !== null && shown.has(intake.mealSlot);
  const timedIntakes = intakes
    .filter((intake) => !atMeal(intake))
    .map((intake) => ({ intake, time: wallTimeInZone(intake.dueAt, timeZone) as string }));

  return (
    <section aria-labelledby="daylog-heading" className="flex flex-col gap-4">
      <h2 id="daylog-heading" className="sr-only">
        {t('nutrition.day.heading')}
      </h2>

      {/* Ein Balken ohne Ziel wäre eine Anzeige ohne Bezugsgröße. Fehlt das
          Ziel, sagt es die Kachel „Zielwerte fehlen“ darüber — einmal, nicht
          dreimal. */}
      {/* Tagesstand mit Ballaststoffen/Zucker/Salz (eingeklappt) in einer
          Kachel. Mit Zielwerten ist das Ziel die einzige kcal-Bezugsgröße;
          die geschätzte Bilanz gibt es nur, solange kein Ziel gesetzt ist. */}
      {/* Ein Teen (27.10.2026) sieht keine Vergleichswerte für Erwachsene
          und keine Bilanz aus Körperzahlen — nur seine eigenen Ziele. */}
      {targets ? (
        summary ? (
          <MacroBars summary={summary}>
            {openNote}
            {estimated ? <EstimatedDay /> : null}
            {child ? null : <DayNutrients entries={entries} />}
          </MacroBars>
        ) : null
      ) : (
        <>
          {openNote}
          {estimated ? <EstimatedDay /> : null}
          {child ? null : <DayNutrients entries={entries} framed />}
          {child ? null : <EnergyBalance date={date} timeZone={timeZone} quietSetup />}
        </>
      )}

      {/* Wasser ist seit 20.10.2026 eine eigene Kachel — vorher steckte es
          in der Makro-Kachel und ließ sich nicht wegklappen. */}
      <WaterTile date={date} today={todayDate} />

      {child ? null : <DayMicros date={date} entries={entries} />}

      {gap !== null ? (
        <p className="kw-tile text-sm text-ink-muted">
          {t('householdMeals.day.proteinGap', { grams: gap })}
        </p>
      ) : null}

      {meals
        ? dayTimeline(meals, trainings, timedIntakes, keep).map((item) =>
            item.kind === 'supplements' ? (
              <SupplementTile
                key={`supplements-${item.time}`}
                time={item.time}
                intakes={item.supplements.map((entry) => entry.intake)}
              />
            ) : item.kind === 'meal' ? (
              <SlotCard
                key={item.meal.slot}
                meal={item.meal}
                skipped={skipped.has(item.meal.slot)}
                date={date}
                entries={entries.filter((entry) => entry.slot === item.meal.slot)}
                placeholders={placeholders.filter((job) => job.slot === item.meal.slot)}
                plans={plans.filter((plan) => plan.slot === item.meal.slot)}
                rows={dayRows}
                supplements={intakes.filter(
                  (intake) => atMeal(intake) && intake.mealSlot === item.meal.slot,
                )}
                household={household.items.filter((entry) => entry.entry.slot === item.meal.slot)}
                householdIds={householdIds}
                mealContext={household.context}
                vegetarian={household.rules.vegetarian}
              />
            ) : (
              <TrainingTile
                key={`${item.training.date}-${item.training.time}`}
                training={item.training}
              />
            ),
          )
        : null}

      <details className="kw-tile">
        <summary className="kw-kicker cursor-pointer">{t('meals.heading')}</summary>
        <div className="mt-2">
          <MealsSetting framed={false} />
        </div>
      </details>

      {household.context && household.householdId ? (
        <details className="kw-tile">
          <summary className="kw-kicker cursor-pointer">
            {t('householdMeals.rules.heading')}
          </summary>
          <div className="mt-2">
            <MealRulesSetting
              key={JSON.stringify(household.rules)}
              slots={(meals ?? []).map((meal) => meal.slot)}
              householdId={household.householdId}
              context={household.context}
              rules={household.rules}
            />
          </div>
        </details>
      ) : null}

      <details className="kw-tile">
        <summary className="kw-kicker cursor-pointer">{t('nutrition.rules.heading')}</summary>
        <ul className="mt-2 list-disc pl-5 text-sm text-ink-muted">
          {NUTRITION_RULES.map((rule) => (
            <li key={rule.de}>{language === 'en' ? rule.en : rule.de}</li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-ink-muted">{t('nutrition.rules.source')}</p>
      </details>
    </section>
  );
}

/** Ein Training im Tagesablauf — an der Stelle, an der es stattfindet. */
function TrainingTile({ training }: { training: UpcomingTraining }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="kw-tile flex items-center justify-between gap-2 border-l-4 border-l-accent">
      <span className="min-w-0">
        <span className="kw-kicker block">{t('meals.training')}</span>
        <span className="block truncate text-sm">
          {training.planDayName ?? t('meals.trainingNoPlan')}
          {' · '}
          {t('meals.trainingDuration', { minutes: training.durationMin })}
        </span>
      </span>
      <span className="kw-figure shrink-0 text-xl">
        {training.sessionId ? t('meals.trainingDone') : training.time}
      </span>
    </div>
  );
}

function SlotCard({
  meal,
  skipped,
  date,
  entries,
  placeholders,
  plans,
  rows,
  supplements,
  household,
  householdIds,
  mealContext,
  vegetarian,
}: {
  meal: MealSetting;
  /** Für diesen Tag ausgelassen: eingeklappt, ohne Erinnerung. */
  skipped: boolean;
  date: string;
  entries: LocalFoodLogEntry[];
  /** Foto-Schätzungen im Hintergrund — offen, ohne Werte. */
  placeholders: LocalEstimateJob[];
  /** Was für diese Mahlzeit geplant ist. */
  plans: LocalMealPlanEntry[];
  /** Alle Protokollzeilen des Tages, auch weich gelöschte. */
  rows: LocalFoodLogEntry[];
  /** Supplements, die an diese Mahlzeit gebunden sind. */
  supplements: DueIntake[];
  /** Haushaltseinträge für diese Mahlzeit (27.10.2026). */
  household: HouseholdItem[];
  /** Protokollzeilen, die zu Haushaltseinträgen gehören — sie stehen dort. */
  householdIds: ReadonlySet<string>;
  mealContext: MealContext | null;
  vegetarian: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const slot = meal.slot;
  const [adding, setAdding] = useState(false);
  // Aufgeklappt am unteren Rand lag das Feld halb hinter der Reiterleiste.
  const addRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (adding) addRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [adding]);

  const [problem, setProblem] = useState<string | null>(null);
  const recipes = useLiveQuery(() => listRecipes(db), [], [] as LocalRecipe[]);
  const recipeById = new Map(recipes.map((recipe) => [recipe.id, recipe]));
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);
  const foodById = new Map(foods.map((food) => [food.id, food]));

  // Offene Schätzungen zählen nicht als 0: ohne Einträge steht „offen“ statt
  // einer Zahl, mit Einträgen „mindestens“ (`totalsWithPending`).
  const totals = totalsWithPending(entries, placeholders.length);
  const total = totals.macros?.kcal ?? 0;
  const protein = totals.macros?.protein ?? 0;

  const name = t(`nutrition.slot.${slot}`);
  const rowById = new Map(rows.map((row) => [row.id, row]));
  const planByRow = new Map(plans.map((plan) => [plannedLogId(plan.id), plan]));
  // Geplant, aber nicht gezählt: ausgelassen (Zeile weich gelöscht) oder noch
  // ohne Zeile — etwa ein Gericht ohne Nährwerte.
  const notCounted = plans.filter((plan) => {
    const row = rowById.get(plannedLogId(plan.id));
    return !row || row.deletedAt;
  });
  const manual = entries.filter((entry) => !planByRow.has(entry.id) && !householdIds.has(entry.id));
  const listed = entries.filter((entry) => !householdIds.has(entry.id));
  const planLabel = (plan: LocalMealPlanEntry) =>
    plannedName(
      plan,
      plan.recipeId ? recipeById.get(plan.recipeId) : undefined,
      plan.foodId ? foodById.get(plan.foodId) : undefined,
      t,
    );

  if (skipped && entries.length === 0 && placeholders.length === 0) {
    return (
      <div className="kw-tile flex items-center justify-between gap-2 opacity-70">
        <span className="min-w-0">
          <h3 className="kw-kicker">{name}</h3>
          <span className="block text-sm text-ink-muted">{t('meals.skipped')}</span>
        </span>
        <button
          type="button"
          onClick={() => void unskipSlot(date, slot, db)}
          aria-label={t('meals.unskipNamed', { name })}
          className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
        >
          {t('meals.unskip')}
        </button>
      </div>
    );
  }

  return (
    <div className="kw-tile">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <h3 className="kw-kicker">{name}</h3>
          {meal.time ? (
            <span className="block text-xs text-ink-muted">
              {meal.reminder ? t('meals.atWithReminder', { time: meal.time }) : meal.time}
            </span>
          ) : null}
        </span>
        {totals.macros ? (
          <p className="shrink-0 text-right">
            {totals.atLeast ? (
              <span className="block text-xs text-warn">
                {t('nutrition.day.photo.pending.atLeast')}
              </span>
            ) : null}
            <span className="kw-figure text-xl">{Math.round(total)}</span>
            <span className="block text-xs text-ink-muted">
              {t('nutrition.macro.kcal')} · {Math.round(protein)} g
            </span>
          </p>
        ) : totals.open > 0 ? (
          <p className="shrink-0 text-xs font-semibold text-warn">
            {t('nutrition.day.photo.pending.slotOpen')}
          </p>
        ) : (
          <p className="shrink-0 text-xs text-ink-muted">{t('nutrition.day.slotEmpty')}</p>
        )}
      </div>

      {household.length > 0 && mealContext ? (
        <ul className="mt-2 flex flex-col gap-1">
          {household.map((item) => (
            <HouseholdRow
              key={item.entry.id}
              item={item}
              rows={rows}
              context={mealContext}
              vegetarian={vegetarian}
            />
          ))}
        </ul>
      ) : null}

      {placeholders.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {placeholders.map((job) => (
            <EstimatePlaceholder key={job.id} job={job} />
          ))}
        </ul>
      ) : null}

      {listed.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {listed.map((entry) => {
            const plan = planByRow.get(entry.id);
            return plan ? (
              <PlannedRow key={entry.id} entry={entry} plan={plan} />
            ) : (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-2 rounded-xl border border-line px-3 py-2 text-sm"
              >
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{entry.label}</span>
                  <span className="text-xs text-ink-muted">
                    {Math.round(entry.kcal)} kcal · {Math.round(entry.protein)} g
                  </span>
                  {entry.estimate ? <EstimateBadge /> : null}
                </span>
                <button
                  type="button"
                  onClick={() => void removeLogEntry(entry.id, db)}
                  aria-label={t('nutrition.day.removeNamed', { name: entry.label })}
                  className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 hover:border-danger hover:text-danger"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {notCounted.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {notCounted.map((plan) => {
            const label = planLabel(plan);
            const wasSkipped = Boolean(rowById.get(plannedLogId(plan.id))?.deletedAt);
            return (
              <li key={plan.id} className="flex items-center justify-between gap-2 px-3 text-sm">
                <span className="min-w-0 break-words text-ink-muted">
                  <span className={wasSkipped ? 'line-through' : ''}>{label}</span>
                  {' · '}
                  {wasSkipped ? t('nutrition.day.plannedSkipped') : t('nutrition.day.notCounted')}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setProblem(null);
                    void restorePlanned(plan, db).then((counted) => {
                      if (!counted) setProblem(t('nutrition.day.plannedWithoutValues'));
                    });
                  }}
                  aria-label={t('nutrition.day.restorePlannedNamed', { name: label })}
                  className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
                >
                  {wasSkipped ? t('meals.unskip') : t('nutrition.day.count')}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {problem ? (
        <p role="alert" className="mt-2 text-xs text-warn">
          {problem}
        </p>
      ) : null}

      {supplements.length > 0 ? (
        <SlotSupplements
          intakes={supplements}
          eaten={entries.length > 0 || placeholders.length > 0}
        />
      ) : null}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          aria-expanded={adding}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('nutrition.day.add')}
        </button>
        {manual.length === 0 && placeholders.length === 0 && !skipped ? (
          <button
            type="button"
            onClick={() => void skipSlot(date, slot, db)}
            aria-label={t('meals.skipNamed', { name })}
            className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
          >
            {t('meals.skip')}
          </button>
        ) : null}
      </div>

      {adding ? (
        <div ref={addRef} className="scroll-mb-28">
          <AddEntry date={date} slot={slot} onDone={() => setAdding(false)} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Ein geplanter Eintrag im Tag: zählt mit, lässt sich in der Menge ändern
 * oder auslassen, wenn etwas dazwischenkommt.
 */
function PlannedRow({
  entry,
  plan,
}: {
  entry: LocalFoodLogEntry;
  plan: LocalMealPlanEntry;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const isRecipe = plan.recipeId !== null;
  const current = isRecipe ? plan.portions : (entry.grams ?? plan.grams ?? 0);
  const [amount, setAmount] = useState(String(current));
  const id = useId();

  return (
    <li className="flex flex-col gap-2 rounded-xl border border-line px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0">
          <span className="block font-semibold break-words">{entry.label}</span>
          <span className="text-xs text-ink-muted">
            {isRecipe
              ? t('nutrition.day.plannedPortionsShort', {
                  count: plan.portions,
                  amount: plan.portions.toLocaleString(i18n.language),
                })
              : `${Math.round(entry.grams ?? 0)} g`}
            {' · '}
            {Math.round(entry.kcal)} kcal · {Math.round(entry.protein)} g ·{' '}
            {t('nutrition.day.fromPlan')}
          </span>
        </span>
        <span className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => {
              setAmount(String(current));
              setEditing((value) => !value);
            }}
            aria-expanded={editing}
            aria-label={t('nutrition.day.changePlannedNamed', { name: entry.label })}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('nutrition.day.change')}
          </button>
          <button
            type="button"
            onClick={() => void skipPlanned(plan.id, db)}
            aria-label={t('nutrition.day.skipPlannedNamed', { name: entry.label })}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('meals.skip')}
          </button>
        </span>
      </div>
      {editing ? (
        <form
          className="flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const value = toNumber(amount);
            if (value === null || value <= 0) return;
            void changePlannedAmount(plan.id, value, db).then(() => setEditing(false));
          }}
        >
          <label htmlFor={id} className="kw-label flex w-28 flex-col gap-1.5">
            {isRecipe ? t('nutrition.recipe.portions') : t('nutrition.recipe.grams')}
            <input
              id={id}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="kw-field"
            />
          </label>
          <button type="submit" className="kw-btn kw-btn-small flex-1">
            {t('nutrition.library.save')}
          </button>
        </form>
      ) : null}
    </li>
  );
}

function AddEntry({
  date,
  slot,
  onDone,
}: {
  date: string;
  slot: DaySlot;
  onDone: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<'quick' | 'recipe' | 'food' | 'free'>('quick');
  // Treffer aus Open Food Facts — eingetragen in Gramm, ohne Bibliothek.
  const [picked, setPicked] = useState<FoodSuggestion | null>(null);

  const recipes = useLiveQuery(() => listRecipes(db), [], [] as LocalRecipe[]);
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);

  const [recipeId, setRecipeId] = useState('');
  const [portions, setPortions] = useState('1');
  const [foodId, setFoodId] = useState('');
  const [grams, setGrams] = useState('100');
  const [label, setLabel] = useState('');
  const [kcal, setKcal] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const ids = { recipe: useId(), portions: useId(), food: useId(), grams: useId(), label: useId() };

  // „Planen“ statt „gegessen“: geht für Gerichte und Lebensmittel aus der
  // Bibliothek — sie landen im Wochenplan und damit auf der Einkaufsliste.
  // Bis 16.10.2026 konnte der Tag nur protokollieren; wer hier „plante“,
  // fand es weder in der Woche noch auf der Liste.
  const canPlan = mode === 'recipe' || (mode === 'food' && !picked);
  const plan = () => {
    setProblem(null);
    if (mode === 'recipe') {
      if (!recipeId) return;
      void planMeal({ date, slot, recipeId, portions: toNumber(portions) ?? 1 }, db).then(onDone);
      return;
    }
    const amount = toNumber(grams);
    if (!foodId || amount === null) return;
    void planMeal({ date, slot, foodId, grams: amount }, db).then(onDone);
  };

  return (
    <form
      className="mt-3 flex flex-col gap-3 rounded-xl border border-accent bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        setProblem(null);

        if (mode === 'recipe') {
          const recipe = recipes.find((entry) => entry.id === recipeId);
          if (!recipe) return;
          void logRecipe({ recipe, date, slot, portions: toNumber(portions) ?? 1 }, db).then(
            (written) => {
              // Ohne Nährwerte wird nichts protokolliert — ein Eintrag mit 0 kcal
              // sähe aus wie eine Angabe.
              if (!written) setProblem(t('nutrition.day.recipeWithoutMacros'));
              else onDone();
            },
          );
          return;
        }

        if (mode === 'quick') return;

        if (mode === 'food' && picked) {
          const amount = toNumber(grams);
          if (amount === null) return;
          void logSuggestion({ suggestion: picked, grams: amount, date, slot }, db).then(
            (written) => {
              if (!written) setProblem(t('quick.incomplete'));
              else onDone();
            },
          );
          return;
        }

        if (mode === 'food') {
          const food = foods.find((entry) => entry.id === foodId);
          const amount = toNumber(grams);
          if (!food || amount === null) return;
          void logFood({ food, date, slot, grams: amount }, db).then(onDone);
          return;
        }

        const kcalValue = toNumber(kcal);
        if (label.trim() === '' || kcalValue === null) return;
        void logFreeEntry(
          {
            date,
            slot,
            label,
            macros: {
              kcal: kcalValue,
              protein: toNumber(protein) ?? 0,
              carbs: toNumber(carbs) ?? 0,
              fat: toNumber(fat) ?? 0,
            },
          },
          db,
        ).then(onDone);
      }}
    >
      <div className="flex flex-wrap gap-1">
        {(['quick', 'recipe', 'food', 'free'] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={mode === option}
            onClick={() => setMode(option)}
            className={`kw-btn kw-btn-small flex-1 ${
              mode === option ? '' : 'kw-btn-quiet text-ink'
            }`}
          >
            {t(`nutrition.day.mode.${option}`)}
          </button>
        ))}
      </div>

      {mode === 'quick' ? <QuickAdd date={date} slot={slot} onDone={onDone} /> : null}

      {mode === 'recipe' ? (
        <div className="flex flex-wrap items-end gap-2">
          <label htmlFor={ids.recipe} className="kw-label flex min-w-40 flex-1 flex-col gap-1.5">
            {t('nutrition.day.pickRecipe')}
            <select
              id={ids.recipe}
              value={recipeId}
              onChange={(event) => setRecipeId(event.target.value)}
              className="kw-field"
            >
              <option value="">{t('nutrition.day.choose')}</option>
              {recipes.map((recipe) => (
                <option key={recipe.id} value={recipe.id}>
                  {recipe.name}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={ids.portions} className="kw-label flex w-24 flex-col gap-1.5">
            {t('nutrition.recipe.portions')}
            <input
              id={ids.portions}
              inputMode="decimal"
              value={portions}
              onChange={(event) => setPortions(event.target.value)}
              className="kw-field"
            />
          </label>
        </div>
      ) : null}

      {mode === 'food' ? (
        <div className="flex flex-wrap items-end gap-2">
          <label htmlFor={ids.food} className="kw-label flex min-w-40 flex-1 flex-col gap-1.5">
            {t('nutrition.day.pickFood')}
            <select
              id={ids.food}
              value={foodId}
              onChange={(event) => setFoodId(event.target.value)}
              className="kw-field"
            >
              <option value="">{t('nutrition.day.choose')}</option>
              {foods.map((food) => (
                <option key={food.id} value={food.id}>
                  {food.name}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={ids.grams} className="kw-label flex w-24 flex-col gap-1.5">
            {t('nutrition.recipe.grams')}
            <input
              id={ids.grams}
              inputMode="decimal"
              value={grams}
              onChange={(event) => setGrams(event.target.value)}
              className="kw-field"
            />
          </label>
        </div>
      ) : null}

      {mode === 'food' ? (
        picked ? (
          <div className="flex items-start justify-between gap-2 rounded-xl border border-line p-3 text-sm">
            <span className="min-w-0 break-words">
              <span className="font-semibold">{picked.name}</span>
              {picked.brand ? <span className="text-ink-muted"> · {picked.brand}</span> : null}
              <span className="block text-xs text-ink-muted">{t('quick.fromOff')}</span>
              {!picked.complete ? (
                <span className="block text-xs text-warn">{t('quick.incomplete')}</span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={() => setPicked(null)}
              className="kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3"
            >
              {t('quick.unpick')}
            </button>
          </div>
        ) : (
          <FoodLookup onPick={setPicked} />
        )
      ) : null}

      {mode === 'free' ? (
        <div className="flex flex-col gap-2">
          {/* Im Hintergrund (28.10.2026): Nach „Schätzen lassen“ steht der
              Platzhalter in dieser Mahlzeit — das Formular schließt. */}
          <PhotoEstimateButton
            date={date}
            slot={slot}
            language={i18n.language}
            onStarted={onDone}
          />
          <label htmlFor={ids.label} className="flex flex-col gap-1 text-xs text-ink-muted">
            {t('nutrition.day.freeLabel')}
            <input
              id={ids.label}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              maxLength={160}
              className="kw-field"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <SmallField label={t('nutrition.macro.kcal')} value={kcal} onChange={setKcal} />
            <SmallField
              label={t('nutrition.macro.protein')}
              value={protein}
              onChange={setProtein}
            />
            <SmallField label={t('nutrition.macro.carbs')} value={carbs} onChange={setCarbs} />
            <SmallField label={t('nutrition.macro.fat')} value={fat} onChange={setFat} />
          </div>
          <p className="text-xs text-ink-muted">{t('nutrition.day.freeHint')}</p>
        </div>
      ) : null}

      {problem ? (
        <p role="alert" className="text-xs text-warn">
          {problem}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {mode === 'quick' ? null : (
          <button type="submit" className="kw-btn flex-1">
            {t('nutrition.day.log')}
          </button>
        )}
        {canPlan ? (
          <button type="button" onClick={plan} className="kw-btn kw-btn-quiet flex-1">
            {t('nutrition.day.plan')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onDone}
          className={`kw-btn kw-btn-quiet ${mode === 'quick' ? 'flex-1' : canPlan ? 'basis-full' : 'basis-2/5'}`}
        >
          {t('nutrition.library.cancel')}
        </button>
      </div>
    </form>
  );
}

/** Dauerhafte Markierung einer geschätzten Protokollzeile — nie nur Farbe. */
function EstimateBadge(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="mt-0.5 block text-xs font-semibold text-warn">
      {t('nutrition.day.photo.badge')}
    </span>
  );
}

/** Offene Foto-Schätzungen: Die Summe ist eine Untergrenze — nie still als 0 gezählt. */
function OpenEstimatesDay({ count }: { count: number }): React.JSX.Element {
  const { t } = useTranslation();
  return <p className="text-xs text-warn">{t('nutrition.day.photo.pending.dayOpen', { count })}</p>;
}

function EstimatedDay(): React.JSX.Element {
  const { t } = useTranslation();
  return <p className="text-xs text-warn">{t('nutrition.day.photo.dayContains')}</p>;
}

/** Name eines Planeintrags — Gericht, Lebensmittel mit Menge oder „unbekannt“. */
export function plannedName(
  entry: { grams: number | null; portions: number },
  recipe: LocalRecipe | undefined,
  food: LocalFood | undefined,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (recipe) {
    return entry.portions === 1
      ? recipe.name
      : t('nutrition.day.plannedPortions', { name: recipe.name, count: entry.portions });
  }
  if (food) {
    return entry.grams !== null
      ? t('nutrition.day.plannedGrams', { name: food.name, grams: Math.round(entry.grams) })
      : food.name;
  }
  return t('nutrition.day.plannedUnknown');
}

function SmallField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <label htmlFor={id} className="kw-label flex w-20 flex-col gap-1.5">
      {label}
      <input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="kw-field"
      />
    </label>
  );
}

/**
 * Supplements zu einer Mahlzeit — „D3 zum Mittag".
 *
 * Ist die Mahlzeit protokolliert und das Supplement noch offen, fragt die
 * Kachel ausdrücklich nach: Das ist der Moment, in dem man es in der Hand hat.
 * Ein Tipp quittiert die Einnahme, und das Telefon fasst nicht mehr nach.
 */
function SlotSupplements({
  intakes,
  eaten,
}: {
  intakes: DueIntake[];
  eaten: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const open = intakes.filter((intake) => !intake.acknowledgedAt && !intake.skippedAt);

  return (
    <div
      className={`mt-3 flex flex-col gap-1 rounded-xl border px-3 py-2 ${
        eaten && open.length > 0 ? 'border-warn' : 'border-line'
      }`}
    >
      {eaten && open.length > 0 ? (
        <p className="text-xs font-semibold text-warn">{t('nutrition.day.supplementsAsk')}</p>
      ) : null}
      <ul className="flex flex-col gap-1">
        {intakes.map((intake) => (
          <IntakeRow key={intake.id} intake={intake} />
        ))}
      </ul>
    </div>
  );
}

/**
 * Supplements zu ihrer Uhrzeit im Tagesablauf (18.10.2026) — vorher standen
 * alle in einer Liste unter den Mahlzeiten, egal wann sie dran waren.
 */
function SupplementTile({
  time,
  intakes,
}: {
  time: string;
  intakes: DueIntake[];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="kw-tile flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="kw-kicker">{t('nutrition.day.supplementsAt')}</h3>
        <span className="kw-figure text-xl">{time}</span>
      </div>
      <ul className="flex flex-col gap-1">
        {intakes.map((intake) => (
          <IntakeRow key={intake.id} intake={intake} />
        ))}
      </ul>
    </section>
  );
}

function IntakeRow({ intake }: { intake: DueIntake }): React.JSX.Element {
  const { t } = useTranslation();
  const label = [intake.supplementName, intake.dose].filter((part) => part !== null).join(' · ');

  return (
    <li className="flex min-h-11 items-center justify-between gap-2 text-sm">
      <span className="min-w-0 truncate">{label}</span>
      {intake.acknowledgedAt ? (
        <span className="shrink-0 text-xs text-good">{t('nutrition.day.supplementTaken')}</span>
      ) : intake.skippedAt ? (
        <span className="shrink-0 text-xs text-warn">{t('nutrition.day.supplementSkipped')}</span>
      ) : (
        <button
          type="button"
          onClick={() => void acknowledgeIntake(intake.id, db)}
          aria-label={t('nutrition.day.supplementTakeNamed', { name: intake.supplementName })}
          className="kw-btn kw-btn-small shrink-0 px-3"
        >
          {t('today.take')}
        </button>
      )}
    </li>
  );
}

/**
 * Schnell eintragen: „Wie gestern", zuletzt Gegessenes, Favoriten — je ein
 * Tipp. Übernommen werden die eingefrorenen Werte des alten Eintrags.
 */
function QuickAdd({
  date,
  slot,
  onDone,
}: {
  date: string;
  slot: DaySlot;
  onDone: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const yesterday = addDays(date, -1);
  const yesterdayEntries = useLiveQuery(
    () => listLog(yesterday, db),
    [yesterday],
    [] as LocalFoodLogEntry[],
  ).filter((entry) => entry.slot === slot);
  const all = useLiveQuery(() => db.food_log.toArray(), [], [] as LocalFoodLogEntry[]);
  const recent = recentMeals(all, { slot, today: date, limit: 6 });
  const favorites = useLiveQuery(() => listRecipes(db), [], [] as LocalRecipe[]).filter(
    (recipe) => recipe.isFavorite,
  );
  const [problem, setProblem] = useState<string | null>(null);
  const kcal = (value: number) => Math.round(value).toLocaleString(i18n.language);

  const nothing = yesterdayEntries.length === 0 && recent.length === 0 && favorites.length === 0;

  return (
    <div className="flex flex-col gap-3">
      {nothing ? <p className="text-sm text-ink-muted">{t('quick.empty')}</p> : null}

      {yesterdayEntries.length > 0 ? (
        <button
          type="button"
          onClick={() => void copyMeal({ date: yesterday, slot }, date, db).then(() => onDone())}
          className="kw-btn kw-btn-small"
        >
          {t('quick.likeYesterday', { count: yesterdayEntries.length })}
        </button>
      ) : null}

      {recent.length > 0 ? (
        <section aria-label={t('quick.recent')} className="flex flex-col gap-1">
          <h4 className="kw-label">{t('quick.recent')}</h4>
          {recent.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => void relogEntry(entry, { date, slot }, db).then(() => onDone())}
              className="flex min-h-11 items-center justify-between gap-2 rounded-xl border border-line px-3 text-left text-sm"
            >
              <span className="min-w-0 truncate">{entry.label}</span>
              <span className="kw-figure shrink-0 text-ink-muted">{kcal(entry.kcal)} kcal</span>
            </button>
          ))}
        </section>
      ) : null}

      {favorites.length > 0 ? (
        <section aria-label={t('quick.favorites')} className="flex flex-col gap-1">
          <h4 className="kw-label">{t('quick.favorites')}</h4>
          {favorites.map((recipe) => (
            <button
              key={recipe.id}
              type="button"
              onClick={() =>
                void logRecipe({ recipe, date, slot }, db).then((written) => {
                  if (!written) setProblem(t('nutrition.day.recipeWithoutMacros'));
                  else onDone();
                })
              }
              className="flex min-h-11 items-center justify-between gap-2 rounded-xl border border-line px-3 text-left text-sm"
            >
              <span className="min-w-0 truncate">★ {recipe.name}</span>
              <span className="shrink-0 text-xs text-ink-muted">{t('quick.onePortion')}</span>
            </button>
          ))}
        </section>
      ) : null}

      {problem ? (
        <p role="alert" className="text-xs text-warn">
          {problem}
        </p>
      ) : null}
    </div>
  );
}
